import React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Slider,
    Typography
} from '@mui/material';
import { Close, Palette } from '@mui/icons-material';
import { PALETTE, alpha} from '../theme';

const ColorPickerModal = ({ 
    open, 
    onClose, 
    color, 
    onColorChange, 
    onPresetClick 
}) => {
    
    // Helper to handle slider changes
    const handleSliderChange = (channel, value) => {
        onColorChange(channel, value);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 8px 25px rgba(0,0,0,0.2)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    overflowX: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1,
                background: 'linear-gradient(45deg, #e8632d, #b8401a)',
                color: 'white',
                borderRadius: '12px 12px 0 0',
                mx: -1,
                mt: -1,
                mb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Palette sx={{ mr: 1 }} />
                    Choose Border Color
                </Box>
                <IconButton 
                    onClick={onClose}
                    sx={{ color: 'white' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Color Preview */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: 3
                }}>
                    <Box sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 3,
                        background: `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`,
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        border: '3px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white',
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                        fontWeight: 'bold'
                    }}>
                        Preview
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* RGB Sliders */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: PALETTE.text }}>
                        Red: {color.r}
                    </Typography>
                    <Slider
                        value={parseInt(color.r)}
                        onChange={(e, value) => handleSliderChange('r', value)}
                        min={0}
                        max={255}
                        sx={{
                            color: '#e74c3c',
                            '& .MuiSlider-thumb': {
                                boxShadow: '0 4px 12px rgba(231, 76, 60, 0.4)',
                            }
                        }}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: PALETTE.text }}>
                        Green: {color.g}
                    </Typography>
                    <Slider
                        value={parseInt(color.g)}
                        onChange={(e, value) => handleSliderChange('g', value)}
                        min={0}
                        max={255}
                        sx={{
                            color: '#27ae60',
                            '& .MuiSlider-thumb': {
                                boxShadow: '0 4px 12px rgba(39, 174, 96, 0.4)',
                            }
                        }}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: PALETTE.text }}>
                        Blue: {color.b}
                    </Typography>
                    <Slider
                        value={parseInt(color.b)}
                        onChange={(e, value) => handleSliderChange('b', value)}
                        min={0}
                        max={255}
                        sx={{
                            color: PALETTE.primary,
                            '& .MuiSlider-thumb': {
                                boxShadow: `0 4px 12px ${alpha('primary', 0.4)}`,
                            }
                        }}
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: PALETTE.text }}>
                        Alpha: {Math.round((color.a / 255) * 100)}%
                    </Typography>
                    <Slider
                        value={parseInt(color.a)}
                        onChange={(e, value) => handleSliderChange('a', value)}
                        min={0}
                        max={255}
                        sx={{
                            color: '#9b59b6',
                            '& .MuiSlider-thumb': {
                                boxShadow: '0 4px 12px rgba(155, 89, 182, 0.4)',
                            }
                        }}
                    />
                </Box>

                {/* Quick Color Presets */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ mb: 2, color: PALETTE.text }}>
                    Quick Colors
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(8, 1fr)' },
                    gap: 1,
                    mb: 2
                }}>
                    {[
                        { r: 0, g: 0, b: 0, a: 255 }, // Black
                        { r: 26, g: 188, b: 156, a: 255 }, // Teal
                        { r: 231, g: 76, b: 60, a: 255 }, // Red
                        { r: 39, g: 174, b: 96, a: 255 }, // Green
                        { r: 52, g: 152, b: 219, a: 255 }, // Blue
                        { r: 155, g: 89, b: 182, a: 255 }, // Purple
                        { r: 241, g: 196, b: 15, a: 255 }, // Yellow
                        { r: 230, g: 126, b: 34, a: 255 }, // Orange
                    ].map((presetColor, index) => (
                        <Box
                            key={index}
                            onClick={() => onPresetClick(presetColor)}
                            sx={{
                                width: '100%',
                                aspectRatio: '1',
                                minWidth: 0,
                                borderRadius: 2,
                                background: `rgba(${presetColor.r}, ${presetColor.g}, ${presetColor.b}, ${presetColor.a})`,
                                cursor: 'pointer',
                                border: '2px solid transparent',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                    border: '2px solid #e8632d',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                }
                            }}
                        />
                    ))}
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 3,
                        background: 'linear-gradient(45deg, #e8632d, #b8401a)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #b8401a, #7e2a0f)',
                        }
                    }}
                >
                    Apply Color
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ColorPickerModal;
